import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Check, X, Zap, Crown, Sparkles, RefreshCw, MessageCircle } from "lucide-react";
import analytics from "../../../../libs/analytics";

interface PricingProps {
  currentPlanId?: string;
  showHeader?: boolean;
}

const plans = [
  {
    id: "1",
    name: "Grátis",
    price: "R$ 0",
    period: "/mês",
    description: "Ideal para testar, perder o medo e entender como o Ataric funciona.",
    features: [
      { text: "Conversação por voz com IA", included: true },
      { text: "Ajuda quando você travar", included: true },
      { text: "5 minutos de conversa por dia", included: true },
      { text: "Sem histórico de progresso", included: false },
    ],
    cta: "Começar grátis",
    popular: false,
    icon: Sparkles,
    checkoutUrl: "https://ataric.com.br/login",
  },
  {
    id: "3",
    name: "Anual",
    price: "R$ 19,90",
    period: "/mês",
    originalPrice: "R$ 598,80",
    finalPrice: "R$ 238,80/ano",
    savings: "Economize R$ 360",
    description: "Para quem quer destravar de vez pagando menos!",
    features: [
      { text: "Conversas por voz ilimitadas", included: true },
      { text: "Prática com temas reais (trabalho, viagem, entrevistas)", included: true },
      { text: "Bônus 4 ebooks exclusivos para acelerar sua jornada rumo à fluência", included: true },
      { text: "Evolução contínua de confiança e fluidez", included: true },
      { text: "Acompanhamento de progresso e habilidades desbloqueadas", included: true },
    ],
    cta: "Escolher anual",
    popular: true,
    icon: Crown,
    checkoutUrl: "https://pay.kiwify.com.br/SNaFvN2",
  },
  {
    id: "4",
    name: "Assinatura",
    price: "R$ 29,90",
    period: "/mês",
    subscriptionNote: "Mínimo 6 meses",
    description: "Preço acessível sem comprometer o limite do cartão!",
    features: [
      { text: "Conversas por voz ilimitadas", included: true },
      { text: "Prática com temas reais (trabalho, viagem, entrevistas)", included: true },
      { text: "Bônus 4 ebooks exclusivos para acelerar sua jornada rumo à fluência", included: true },
      { text: "Evolução contínua de confiança e fluidez", included: true },
      { text: "Acompanhamento de progresso e habilidades desbloqueadas", included: true },
      { text: "Cobrança mensal no cartão (não bloqueia limite)", included: true },
    ],
    cta: "Assinar agora",
    popular: false,
    icon: RefreshCw,
    checkoutUrl: "https://pay.kiwify.com.br/0trynhX",
  },
  {
    id: "2",
    name: "Mensal",
    price: "R$ 49,90",
    period: "/mês",
    description: "Teste o hábito de falar inglês sem compromisso de longo prazo.",
    features: [
      { text: "Conversas por voz ilimitadas", included: true },
      { text: "Prática com temas reais (trabalho, viagem, entrevistas)", included: true },
      { text: "Evolução contínua de confiança e fluidez", included: true },
      { text: "Acompanhamento de progresso e habilidades desbloqueadas", included: true },
    ],
    cta: "Começar agora",
    popular: false,
    icon: Zap,
    checkoutUrl: "https://pay.kiwify.com.br/zJ9PYfm",
  },
];

const Pricing = ({ currentPlanId, showHeader = true }: PricingProps) => {
  const isCurrentPlan = (planId: string) => currentPlanId === planId;
  const isUpgrade = (planId: string) => {
    if (!currentPlanId) return true;
    const currentNum = parseInt(currentPlanId);
    // Plan hierarchy: 3 (Anual) = 4 (Assinatura) > 2 (Mensal) > 1 (Grátis)
    // Plans 3 and 4 are equivalent (same tier)
    if (currentNum === 3 || currentNum === 4) return false; // Already has best plan tier
    if (currentNum === 2) return planId === "3" || planId === "4"; // Can upgrade to Anual or Assinatura
    return planId !== "1"; // Free users can upgrade to any paid plan
  };

  return (
    <section id="planos" className="py-20 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 hero-gradient" />
      
      <div className="container mx-auto px-4 relative z-10">
        {showHeader && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Escolha seu plano e{" "}
              <span className="text-gradient glow-text">comece a falar hoje</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Invista em você. Sua fluência em inglês está a um passo de distância.
            </p>
          </motion.div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {plans.map((plan, index) => {
            const isCurrent = isCurrentPlan(plan.id);
            const canUpgrade = isUpgrade(plan.id);
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                {isCurrent && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-green-600 text-white text-sm font-bold px-4 py-1 rounded-full shadow-lg">
                      PLANO ATUAL
                    </span>
                  </div>
                )}
                {!isCurrent && plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <span className="bg-primary text-primary-foreground text-sm font-bold px-4 py-1 rounded-full shadow-lg">
                      MAIS POPULAR
                    </span>
                  </div>
                )}
                
                <div className={`card-glass rounded-2xl p-8 h-full flex flex-col ${
                  isCurrent
                    ? "border-green-500/50 shadow-[0_0_50px_hsl(142_76%_36%/0.25)]"
                    : plan.popular 
                      ? "border-primary/50 shadow-[0_0_50px_hsl(277_78%_40%/0.25)]" 
                      : ""
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      isCurrent ? "bg-green-500/20" : plan.popular ? "bg-primary/20" : "bg-secondary"
                    }`}>
                      <plan.icon className={`w-6 h-6 ${
                        isCurrent ? "text-green-500" : plan.popular ? "text-primary" : "text-muted-foreground"
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-display text-xl font-bold text-foreground">{plan.name}</h3>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-4xl font-bold text-foreground">{plan.price}</span>
                      <span className="text-muted-foreground">{plan.period}</span>
                    </div>
                    {plan.originalPrice && (
                      <div className="mt-2">
                        <span className="text-muted-foreground line-through text-sm">{plan.originalPrice}</span>
                        <span className="ml-2 text-primary text-sm font-medium">{plan.savings}</span>
                      </div>
                    )}
                    {plan.finalPrice && (
                      <div className="mt-1">
                        <span className="text-foreground/80 text-sm">{plan.finalPrice}</span>
                      </div>
                    )}
                    {plan.subscriptionNote && (
                      <div className="mt-2">
                        <span className="bg-amber-500/20 text-amber-400 text-xs font-semibold px-2 py-1 rounded">
                          {plan.subscriptionNote}
                        </span>
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          feature.included ? "bg-primary/20" : "bg-destructive/20"
                        }`}>
                          {feature.included ? (
                            <Check className="w-3 h-3 text-primary" />
                          ) : (
                            <X className="w-3 h-3 text-destructive" />
                          )}
                        </span>
                        <span className={feature.included ? "text-muted-foreground" : "text-muted-foreground/50"}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {isCurrent ? (
                    <Button 
                      variant="ctaOutline" 
                      size="xl" 
                      className="w-full opacity-50 cursor-not-allowed"
                      disabled
                    >
                      Plano atual
                    </Button>
                  ) : canUpgrade ? (
                    <Button 
                      variant={plan.popular ? "cta" : "ctaOutline"} 
                      size="xl" 
                      className="w-full"
                      asChild
                    >
                      <a href={plan.checkoutUrl} onClick={() => analytics.landingPlanClicked(plan.name, plan.id)}>{plan.cta}</a>
                    </Button>
                  ) : (
                    <Button 
                      variant="ctaOutline" 
                      size="xl" 
                      className="w-full opacity-50 cursor-not-allowed"
                      disabled
                    >
                      Plano inferior
                    </Button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
        
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-12"
        >
          <p className="text-sm text-muted-foreground">
            Em caso de parcelamento, podem ser aplicados juros pela operadora do cartão.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-8"
        >
          <a 
            href="https://api.whatsapp.com/send/?phone=554391832935&text=Ol%C3%A1%21+Preciso+de+ajuda+com+o+ATARIC.&type=phone_number&app_absent=0" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-full transition-colors"
            onClick={() => analytics.landingWhatsappClicked('pricing_consultant')}
          >
            <MessageCircle className="w-5 h-5" />
            Fale com um consultor antes de assinar
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
